
import React, { createContext, useContext } from "react";
import { useQuery, useMutation, useQueryClient, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { fitnessPlan, mindfulnessPlan, nutritionPlan, supplementPlan } from "@/data/wellnessPlans";

// Define the types for our different wellness plans
interface WellnessPlanItem {
  completed: boolean;
  [key: string]: any;
}

interface WellnessPlan {
  progress: number;
  [key: string]: any;
}

interface WellnessContextType {
  nutritionPlan: WellnessPlan;
  supplementPlan: WellnessPlan;
  fitnessPlan: WellnessPlan;
  mindfulnessPlan: WellnessPlan;
  toggleItemCompletion: (planType: string, itemIndex: number) => void;
  calculateNutritionScore: () => number;
  calculateFitnessScore: () => number;
  calculateSupplementScore: () => number;
  calculateMindfulnessScore: () => number;
  calculateWellnessScore: () => number;
  getSleepScore: () => number;
}

// Create the context
const WellnessContext = createContext<WellnessContextType | undefined>(undefined);

// Create a custom React Query client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Define the provider component
export const WellnessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();

  // Function to calculate progress
  const calculateProgress = (items: WellnessPlanItem[]): number => {
    if (items.length === 0) return 0;
    const completedItems = items.filter(item => item.completed).length;
    return Math.round((completedItems / items.length) * 100);
  };

  // Mock API functions (replace with real API calls when connecting to Supabase)
  const fetchWellnessPlans = async (): Promise<{
    nutrition: WellnessPlan;
    supplement: WellnessPlan;
    fitness: WellnessPlan;
    mindfulness: WellnessPlan;
  }> => {
    // Simulating API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Return our local data
    return {
      nutrition: nutritionPlan,
      supplement: supplementPlan,
      fitness: fitnessPlan,
      mindfulness: mindfulnessPlan,
    };
  };

  // Use React Query to fetch wellness plans
  const { data: wellnessPlans = { 
    nutrition: nutritionPlan,
    supplement: supplementPlan,
    fitness: fitnessPlan,
    mindfulness: mindfulnessPlan
  } } = useQuery({
    queryKey: ['wellnessPlans'],
    queryFn: fetchWellnessPlans,
  });

  // Mutation for toggling completion status
  const toggleCompletionMutation = useMutation({
    mutationFn: async ({ planType, itemIndex }: { planType: string, itemIndex: number }) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // In a real app, this would update the backend
      console.log(`Toggling ${planType} item at index ${itemIndex}`);
      return { planType, itemIndex };
    },
    onMutate: async ({ planType, itemIndex }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['wellnessPlans'] });
      
      // Get the current plans
      const previousPlans = queryClient.getQueryData(['wellnessPlans']);
      
      // Optimistically update the UI
      queryClient.setQueryData(['wellnessPlans'], (old: any) => {
        const newPlans = { ...old };
        
        // Create deep copy of the plan to update
        const planKey = planType.toLowerCase();
        const planCopy = JSON.parse(JSON.stringify(newPlans[planKey]));
        
        // Get the appropriate items array based on plan type
        let items;
        if (planKey === 'nutrition') items = planCopy.meals;
        else if (planKey === 'supplement') items = planCopy.supplements;
        else if (planKey === 'fitness') items = planCopy.exercises;
        else if (planKey === 'mindfulness') items = planCopy.practices;
        
        // Toggle the completed status
        if (items && items[itemIndex]) {
          items[itemIndex].completed = !items[itemIndex].completed;
          planCopy.progress = calculateProgress(items);
        }
        
        // Update the plan in our query data
        newPlans[planKey] = planCopy;
        return newPlans;
      });
      
      return { previousPlans };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousPlans) {
        queryClient.setQueryData(['wellnessPlans'], context.previousPlans);
      }
    },
    onSettled: () => {
      // Refetch after error or success to ensure our data is correct
      queryClient.invalidateQueries({ queryKey: ['wellnessPlans'] });
    },
  });

  // Function to toggle item completion
  const toggleItemCompletion = (planType: string, itemIndex: number) => {
    toggleCompletionMutation.mutate({ planType, itemIndex });
  };

  // Score calculation functions
  const calculateNutritionScore = (): number => {
    const plan = wellnessPlans.nutrition;
    
    if (!plan || !plan.meals || plan.meals.length === 0) {
      return 0;
    }
    
    // Base score from progress
    let score = plan.progress;
    
    // Additional factors could be considered:
    // - Meal balance (protein, carbs, fats)
    // - Calorie adherence
    // - Water intake
    // - Consistency over time
    
    // For now, we'll just return the progress value
    return score;
  };
  
  const calculateFitnessScore = (): number => {
    const plan = wellnessPlans.fitness;
    
    if (!plan || !plan.exercises || plan.exercises.length === 0) {
      return 0;
    }
    
    // Base score from progress
    let score = plan.progress;
    
    // Additional factors could be considered:
    // - Workout intensity
    // - Variety of exercises
    // - Consistency over time
    // - Meeting weekly targets
    
    // For now, we'll just return the progress value
    return score;
  };
  
  const calculateSupplementScore = (): number => {
    const plan = wellnessPlans.supplement;
    
    if (!plan || !plan.supplements || plan.supplements.length === 0) {
      return 0;
    }
    
    // Base score from progress
    let score = plan.progress;
    
    // Additional factors could be considered:
    // - Taking at correct times
    // - Appropriate dosages
    // - Consistency over time
    
    // For now, we'll just return the progress value
    return score;
  };
  
  const calculateMindfulnessScore = (): number => {
    const plan = wellnessPlans.mindfulness;
    
    if (!plan || !plan.practices || plan.practices.length === 0) {
      return 0;
    }
    
    // Base score from progress
    let score = plan.progress;
    
    // Additional factors could be considered:
    // - Duration of practices
    // - Variety of techniques
    // - Consistency over time
    
    // For now, we'll just return the progress value
    return score;
  };
  
  const getSleepScore = (): number => {
    // In a real implementation, this would be calculated from sleep data
    // For now, return a placeholder value
    return 78;
  };
  
  const calculateWellnessScore = (): number => {
    // Weighted average of all wellness scores
    const nutritionWeight = 0.25;
    const fitnessWeight = 0.25;
    const supplementWeight = 0.15;
    const mindfulnessWeight = 0.20;
    const sleepWeight = 0.15;
    
    const nutritionScore = calculateNutritionScore();
    const fitnessScore = calculateFitnessScore();
    const supplementScore = calculateSupplementScore();
    const mindfulnessScore = calculateMindfulnessScore();
    const sleepScore = getSleepScore();
    
    const totalScore = (
      nutritionScore * nutritionWeight +
      fitnessScore * fitnessWeight +
      supplementScore * supplementWeight +
      mindfulnessScore * mindfulnessWeight +
      sleepScore * sleepWeight
    );
    
    return Math.round(totalScore);
  };

  // Provide the context value
  const contextValue: WellnessContextType = {
    nutritionPlan: wellnessPlans.nutrition,
    supplementPlan: wellnessPlans.supplement,
    fitnessPlan: wellnessPlans.fitness,
    mindfulnessPlan: wellnessPlans.mindfulness,
    toggleItemCompletion,
    calculateNutritionScore,
    calculateFitnessScore,
    calculateSupplementScore,
    calculateMindfulnessScore,
    calculateWellnessScore,
    getSleepScore
  };

  return (
    <WellnessContext.Provider value={contextValue}>
      {children}
    </WellnessContext.Provider>
  );
};

// Create a custom hook to use the wellness context
export const useWellness = (): WellnessContextType => {
  const context = useContext(WellnessContext);
  if (context === undefined) {
    throw new Error("useWellness must be used within a WellnessProvider");
  }
  return context;
};

// Wrapper component to provide both QueryClient and WellnessContext
export const WellnessQueryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <WellnessProvider>{children}</WellnessProvider>
    </QueryClientProvider>
  );
};
